import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ResidentialPage = () => {
  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Slash Your Energy Bills',
      description: 'Save up to 70% on monthly electricity costs and protect yourself from rising utility rates.',
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Boost Home Value',
      description: 'Homes with solar panels sell for 4% more on average and attract buyers faster.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Reduce Carbon Footprint',
      description: 'A typical residential solar system offsets 3-4 tons of carbon emissions annually.',
    },
    {
      icon: 'ri-shield-check-line',
      title: '25-Year Warranty',
      description: 'Premium equipment backed by industry-leading warranties for peace of mind.',
    },
  ];

  const process = [
    {
      step: '1',
      title: 'Free Home Assessment',
      description: 'We evaluate your roof, energy usage, and sun exposure to design the perfect system.',
      icon: 'ri-home-4-line',
    },
    {
      step: '2',
      title: 'Custom System Design',
      description: 'Our engineers create a tailored solar solution optimized for maximum savings.',
      icon: 'ri-draft-line',
    },
    {
      step: '3',
      title: 'Professional Installation',
      description: 'Certified technicians install your system with precision, typically in 1-3 days.',
      icon: 'ri-tools-line',
    },
    {
      step: '4',
      title: 'Activation & Monitoring',
      description: 'We handle all permits and inspections, then activate your system for immediate savings.',
      icon: 'ri-flashlight-line',
    },
  ];

  const gallery = [
    {
      image: 'https://public.readdy.ai/ai/img_res/2f411da10f37e99c8583f522fcccdb31.jpg',
      title: 'Modern Suburban Home',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Two%20story%20residential%20house%20with%20solar%20panel%20array%20on%20roof%2C%20traditional%20home%20design%2C%20professional%20installation%2C%20clean%20roofline%2C%20sunny%20day%2C%20energy%20independence%2C%20sustainable%20living%2C%20well%20designed%20solar%20system&width=600&height=400&seq=res-gallery-2&orientation=landscape',
      title: 'Two-Story Installation',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Ranch%20style%20home%20with%20complete%20solar%20panel%20coverage%20on%20roof%2C%20single%20story%20house%2C%20efficient%20energy%20system%2C%20residential%20solar%20installation%2C%20clean%20modern%20look%2C%20Florida%20residential%20property%2C%20blue%20sky%20background&width=600&height=400&seq=res-gallery-3&orientation=landscape',
      title: 'Ranch Style Home',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Contemporary%20home%20with%20sleek%20solar%20panels%20integrated%20into%20roof%20design%2C%20modern%20architecture%2C%20luxury%20residential%20property%2C%20high%20end%20solar%20installation%2C%20clean%20energy%20solution%2C%20beautiful%20home%20exterior%2C%20professional%20workmanship&width=600&height=400&seq=res-gallery-4&orientation=landscape',
      title: 'Contemporary Design',
    },
  ];

  return (
    <div className="font-poppins">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20family%20home%20with%20solar%20panels%20on%20roof%2C%20happy%20family%20standing%20in%20front%20yard%2C%20parents%20and%20children%2C%20suburban%20neighborhood%2C%20clean%20energy%20lifestyle%2C%20sustainable%20living%2C%20modern%20residential%20property%2C%20sunny%20day%2C%20welcoming%20atmosphere%2C%20American%20dream%20home&width=1920&height=1080&seq=residential-hero&orientation=landscape"
            alt="Family home with solar panels"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-solar-blue/75 via-solar-blue/55 to-solar-blue/75"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Power Your Home with<br />
            <span className="text-energy-yellow">Clean Solar Energy</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Transform your home into a clean energy powerhouse. Save money, increase property value, and enjoy energy independence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/contact"
              className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              See Your Savings
            </Link>
            <a
              href="tel:352-509-6933"
              className="bg-white text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              352-509-6933
            </a>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Why <strong>Residential Solar</strong> Makes Sense
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover the powerful advantages of installing solar panels on your home.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                  <i className={`${benefit.icon} text-3xl text-solar-blue`}></i>
                </div>
                <h3 className="text-xl font-bold text-solar-blue mb-3">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Simple <strong>Installation Process</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From consultation to activation, we make going solar easy and stress-free.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-eco-green/10 rounded-full mb-6">
                  <i className={`${item.icon} text-3xl text-eco-green`}></i>
                </div>
                <div className="text-5xl font-bold text-energy-yellow/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-solar-blue mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly Savings */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
                Start Saving <strong>Immediately</strong>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                The average Ocala homeowner saves over $100 per month on electricity bills with solar. That's $1,200+ in annual savings and over $30,000 in savings over 25 years.
              </p>

              <div className="space-y-6">
                <div className="bg-soft-gray p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">Before Solar</span>
                    <span className="text-2xl font-bold text-red-500">$180/mo</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-3">
                    <div className="bg-red-500 h-3 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>

                <div className="bg-soft-gray p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">After Solar</span>
                    <span className="text-2xl font-bold text-eco-green">$60/mo</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-3">
                    <div className="bg-eco-green h-3 rounded-full" style={{ width: '33%' }}></div>
                  </div>
                </div>

                <div className="bg-energy-yellow/10 border-2 border-energy-yellow p-6 rounded-xl">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-solar-blue mb-2">$120/mo</div>
                    <div className="text-sm font-semibold text-gray-700">Average Monthly Savings</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20homeowner%20looking%20at%20reduced%20electricity%20bill%20with%20solar%20panels%20visible%20on%20roof%20in%20background%2C%20satisfied%20customer%2C%20cost%20savings%2C%20financial%20benefits%2C%20residential%20solar%20success%20story%2C%20money%20saving%2C%20affordable%20energy%2C%20bright%20natural%20lighting&width=800&height=1000&seq=savings-happy&orientation=portrait"
                  alt="Happy homeowner with savings"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Warranty & Maintenance */}
      <section className="py-20 bg-solar-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Comprehensive <strong>Warranty & Support</strong>
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Your investment is protected with industry-leading warranties and ongoing maintenance support.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm p-8 rounded-xl"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6 mx-auto">
                <i className="ri-shield-star-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">25-Year Panel Warranty</h3>
              <p className="text-sm text-white/80 text-center">
                Premium solar panels guaranteed to maintain 85% efficiency after 25 years.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm p-8 rounded-xl"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6 mx-auto">
                <i className="ri-tools-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">10-Year Workmanship</h3>
              <p className="text-sm text-white/80 text-center">
                Our installation work is backed by a comprehensive 10-year workmanship warranty.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm p-8 rounded-xl"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6 mx-auto">
                <i className="ri-customer-service-2-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">Lifetime Support</h3>
              <p className="text-sm text-white/80 text-center">
                24/7 monitoring and local support team ready to assist whenever you need us.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Our <strong>Recent Installations</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See the quality and craftsmanship of our residential solar installations throughout Ocala.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {gallery.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-solar-blue/80 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white">{item.title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
              Ready to Power Your Home with Solar?
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Get a free custom solar assessment and discover exactly how much you can save.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                See Your Savings
              </Link>
              <a
                href="tel:352-509-6933"
                className="bg-solar-blue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-solar-blue/90 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call 352-509-6933
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResidentialPage;